import React from 'react';
import {
  ArrowLeft, Search, Package, Plus, ShoppingCart, Tag, Euro, Sparkles,
} from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import LiquidGlassButton from './LiquidGlassButton';
import { buildSearchableText, matchesAllTerms } from 'core/utils/search';
import FilterBar from './products/FilterBar';
import SortBar from './products/SortBar';
import PriceRangeFilter from './products/PriceRangeFilter';
import { sortProducts } from './products/sortProducts';
import { getUserPreference, setUserPreference } from 'core/api/apiService';

const MATERIAL_TYPE_OPTIONS = ['ROH', 'HALB', 'HAWA', 'DIEN', 'UNBW'];
const PRODUCT_STATUS_OPTIONS = ['active', 'blocked', 'discontinued'];

// Default-Felder, die beim ersten Besuch sichtbar sind
const DEFAULT_VISIBLE_FILTER_IDS = ['material_type', 'status', 'category'];

// Seitengroesse fuer die Client-Pagination (F10) — 24 = 3x8 im Grid
const PRODUCTS_PAGE_SIZE = 24;

// Debounce-Helfer fuer die Persistenz-Speicherung
function useDebouncedCallback(fn, delay) {
  const timer = React.useRef(null);
  return React.useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]); // eslint-disable-line react-hooks/exhaustive-deps
}

const ProductView = ({
  selectedCatalog,
  products,
  searchTerm,
  loading,
  onBack,
  onSearchChange,
  onAddToCart,
  onSelectProduct,
  user,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // ── Filter-State ──────────────────────────────────────────────────────────
  const [selectedMaterialTypes, setSelectedMaterialTypes] = React.useState([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState([]);
  const [selectedCategories, setSelectedCategories] = React.useState([]);
  // { [char_name]: string[] }
  const [selectedAttrs, setSelectedAttrs] = React.useState({});
  const [selectedManufacturers, setSelectedManufacturers] = React.useState([]);
  // Preis-Spanne (F10) — '' = keine Grenze
  const [priceMin, setPriceMin] = React.useState('');
  const [priceMax, setPriceMax] = React.useState('');

  // Welche Filter-Felder in der Top-Leiste sichtbar sind (user-konfigurierbar)
  const [visibleFilterIds, setVisibleFilterIds] = React.useState(DEFAULT_VISIBLE_FILTER_IDS);
  const [prefsLoaded, setPrefsLoaded] = React.useState(false);

  // ── Sortierung + Client-Pagination (F10) ──────────────────────────────────
  // sortByManual = null, solange der User nicht aktiv waehlt → Default ableiten:
  // Relevanz bei aktiver Suche, sonst Name A–Z.
  const [sortByManual, setSortByManual] = React.useState(null);
  const [visibleCount, setVisibleCount] = React.useState(PRODUCTS_PAGE_SIZE);
  const effectiveSort = sortByManual || (searchTerm ? 'relevance' : 'name_asc');

  // Bei Aenderung von Suche/Sortierung/Filter wieder auf die erste Seite springen
  React.useEffect(() => {
    setVisibleCount(PRODUCTS_PAGE_SIZE);
  }, [searchTerm, effectiveSort, selectedMaterialTypes, selectedStatuses, selectedCategories, selectedManufacturers, selectedAttrs, priceMin, priceMax]);

  // ── Persistenz laden (Mount) ──────────────────────────────────────────────
  React.useEffect(() => {
    if (!user) { setPrefsLoaded(true); return; }
    let cancelled = false;
    getUserPreference(user, 'product_filters')
      .then(res => {
        if (cancelled) return;
        if (res && Array.isArray(res.value) && res.value.length > 0) {
          setVisibleFilterIds(res.value);
        } else {
          setVisibleFilterIds(DEFAULT_VISIBLE_FILTER_IDS);
        }
      })
      .catch(() => {
        if (!cancelled) setVisibleFilterIds(DEFAULT_VISIBLE_FILTER_IDS);
      })
      .finally(() => {
        if (!cancelled) setPrefsLoaded(true);
      });
    return () => { cancelled = true; };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persistenz speichern (debounced) ────────────────────────────────────
  const persistPrefs = React.useCallback((ids) => {
    if (!user) return;
    setUserPreference(user, 'product_filters', ids).catch(() => {
      // Silent fail — Präferenz geht verloren, kein Blocker
    });
  }, [user]);

  const debouncedPersist = useDebouncedCallback(persistPrefs, 600);

  const handleChangeVisible = (ids) => {
    setVisibleFilterIds(ids);
    debouncedPersist(ids);
  };

  // ── Facetten-Ableitung ───────────────────────────────────────────────────
  const { categoryFacet, attributeFacets, categoryLabelMap, manufacturerFacet } = React.useMemo(() => {
    const catCounts = {};
    const catLabels = {};
    const manuCounts = {};
    // { [char_group]: { [char_name]: { label, sortOrder, values: { [value]: count } } } }
    const attrMap = {};

    (products || []).forEach(product => {
      if (product.category) {
        catCounts[product.category] = (catCounts[product.category] || 0) + 1;
        if (!catLabels[product.category] && product.category_label) {
          catLabels[product.category] = product.category_label;
        }
      }

      if (product.manufacturer) {
        manuCounts[product.manufacturer] = (manuCounts[product.manufacturer] || 0) + 1;
      }

      if (Array.isArray(product.attributes)) {
        product.attributes.forEach(attr => {
          const group = attr.char_group || '';
          const charName = attr.char_name || '';
          const label = attr.label || charName;
          const value = attr.value != null ? String(attr.value) : '';
          if (!charName || !value) return;

          if (!attrMap[group]) attrMap[group] = {};
          if (!attrMap[group][charName]) {
            attrMap[group][charName] = {
              label,
              sortOrder: attr.sort_order != null ? attr.sort_order : 9999,
              values: {},
            };
          }
          attrMap[group][charName].values[value] =
            (attrMap[group][charName].values[value] || 0) + 1;
        });
      }
    });

    const categoryFacet = Object.entries(catCounts).sort(([, a], [, b]) => b - a);
    const manufacturerFacet = Object.entries(manuCounts).sort(([, a], [, b]) => b - a);
    const categoryLabelMap = catLabels;
    const attributeFacets = Object.entries(attrMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, charMap]) => {
        const chars = Object.entries(charMap)
          .sort(([, a], [, b]) => {
            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
            return a.label.localeCompare(b.label);
          })
          .map(([charName, info]) => {
            const sortedValues = Object.entries(info.values).sort(([, a], [, b]) => b - a);
            return { charName, label: info.label, values: sortedValues };
          });
        return { group, chars };
      });

    return { categoryFacet, attributeFacets, categoryLabelMap, manufacturerFacet };
  }, [products]);

  // ── Verfuegbare Filter-Felder (mandanten-spezifisch, aus Daten abgeleitet) ──
  const availableFilters = React.useMemo(() => {
    const list = [];

    // Feste Produktfelder
    list.push({ id: 'material_type', label: t('products.filterMaterialType'), kind: 'fixed' });
    list.push({ id: 'status', label: t('products.filterStatus'), kind: 'fixed' });
    if (categoryFacet.length > 0) {
      list.push({ id: 'category', label: t('products.filterCategory'), kind: 'fixed' });
    }
    if (manufacturerFacet.length > 0) {
      list.push({ id: 'manufacturer', label: t('products.filterManufacturer'), kind: 'fixed' });
    }

    // Klassifizierungs-Merkmale
    attributeFacets.forEach(({ group, chars }) => {
      chars.forEach(({ charName, label }) => {
        list.push({
          id: `attr:${charName}`,
          label,
          kind: 'attr',
          group: group || t('products.filterAttributes'),
        });
      });
    });

    return list;
  }, [categoryFacet, attributeFacets, manufacturerFacet, t]);

  // ── Gefiltertes Ergebnis ─────────────────────────────────────────────────
  const filteredProducts = (products || []).filter(product => {
    if (Array.isArray(selectedMaterialTypes) && selectedMaterialTypes.length > 0) {
      if (!selectedMaterialTypes.includes(product.material_type)) return false;
    }
    if (Array.isArray(selectedStatuses) && selectedStatuses.length > 0) {
      const productStatus = product.status || 'active';
      if (!selectedStatuses.includes(productStatus)) return false;
    }
    if (Array.isArray(selectedCategories) && selectedCategories.length > 0) {
      if (!selectedCategories.includes(product.category)) return false;
    }
    if (Array.isArray(selectedManufacturers) && selectedManufacturers.length > 0) {
      if (!selectedManufacturers.includes(product.manufacturer)) return false;
    }
    // Attribut-Filter: AND ueber Merkmale, OR innerhalb
    const attrEntries = Object.entries(selectedAttrs).filter(([, vals]) => vals.length > 0);
    if (attrEntries.length > 0) {
      const productAttrs = Array.isArray(product.attributes) ? product.attributes : [];
      for (const [charName, allowedVals] of attrEntries) {
        const hit = productAttrs.some(
          a => a.char_name === charName && allowedVals.includes(String(a.value ?? ''))
        );
        if (!hit) return false;
      }
    }
    // Preis-Spanne (F10): leere Grenze = offen; bei aktiver Grenze keine Nicht-Zahlen
    if (priceMin !== '' || priceMax !== '') {
      const priceNum = parseFloat(product.price);
      if (!Number.isFinite(priceNum)) return false;
      if (priceMin !== '' && priceNum < parseFloat(priceMin)) return false;
      if (priceMax !== '' && priceNum > parseFloat(priceMax)) return false;
    }
    // Volltext
    const searchableText = buildSearchableText(
      product.name, product.description, product.category, product.sku,
      product.vendor_mat, product.vendormat, product.vendor,
      product.preferred_vendor, product.fixed_vendor,
      product.unit, product.price, product.currency,
      product.material_type, product.ean
    );
    return matchesAllTerms(searchableText, searchTerm);
  });

  const hasAnyFilter =
    selectedMaterialTypes.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedCategories.length > 0 ||
    selectedManufacturers.length > 0 ||
    Object.keys(selectedAttrs).length > 0 ||
    priceMin !== '' ||
    priceMax !== '';

  // ── Sortierung anwenden + Seite ausschneiden (F10) ────────────────────────
  const sortedProducts = sortProducts(filteredProducts, effectiveSort);
  const displayedProducts = sortedProducts.slice(0, visibleCount);
  const canLoadMore = displayedProducts.length < sortedProducts.length;

  const resetAllFilters = () => {
    setSelectedMaterialTypes([]);
    setSelectedStatuses([]);
    setSelectedCategories([]);
    setSelectedManufacturers([]);
    setSelectedAttrs({});
    setPriceMin('');
    setPriceMax('');
  };

  // ── Style-Helfer ─────────────────────────────────────────────────────────
  const cardCls = isDarkMode
    ? 'bg-gray-900/40 border border-white/15 backdrop-blur-xl rounded-2xl'
    : 'glass-light-card';
  const headingCls = isDarkMode ? 'text-white' : 'text-slate-900';
  const subCls = isDarkMode ? 'text-white/60' : 'text-slate-500';

  const facets = { categoryFacet, attributeFacets, categoryLabelMap, manufacturerFacet };

  return (
    <div className={`${isDarkMode ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black' : 'bg-[#F8FAFC]'}`}>
      {/* ── Header: Zurueck, Katalog, Suche ────────────────────────────── */}
      <div className="relative">
        <div className={`absolute inset-0 ${
          isDarkMode
            ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
            : 'bg-white border-b border-slate-200'
        }`}></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <LiquidGlassButton onClick={onBack} variant="secondary" size="medium" icon={ArrowLeft} iconPosition="left">
                {t('products.backToCatalogs')}
              </LiquidGlassButton>
              <div className={`h-6 w-px ${isDarkMode ? 'bg-white/20' : 'bg-slate-200'}`}></div>
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                  isDarkMode ? 'bg-gray-800/60 border border-white/15' : 'bg-brand/10'
                }`}>
                  {selectedCatalog?.icon ? (
                    <span className="text-xl">{selectedCatalog.icon}</span>
                  ) : (
                    <Package className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
                  )}
                </div>
                <div>
                  <h1 className={`text-2xl font-bold ${headingCls}`}>{selectedCatalog?.name}</h1>
                  <p className={subCls}>{t('products.supplierBy', { supplier: selectedCatalog?.supplier })}</p>
                </div>
              </div>
            </div>

            {/* Suche */}
            <div className="relative w-full max-w-md group/search">
              <div className={`absolute inset-0 rounded-2xl ${
                isDarkMode
                  ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
              }`}></div>
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <Search className={`w-5 h-5 ${
                  isDarkMode
                    ? 'text-white/60 group-focus-within/search:text-blue-300/80'
                    : 'text-slate-400 group-focus-within/search:text-brand'
                } transition-colors duration-300`} />
              </div>
              <input
                type="text"
                placeholder={t('products.search')}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className={`relative w-full pl-12 pr-4 py-3.5 bg-transparent border-0 rounded-2xl focus:ring-2 focus:outline-none transition-all duration-300 ${
                  isDarkMode
                    ? 'focus:ring-blue-400/50 text-white placeholder-white/60'
                    : 'focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* KI-Tipp Banner (Demo) */}
        <div className={`mb-5 rounded-2xl p-4 flex items-start gap-3 ${
          isDarkMode ? 'bg-blue-900/20 border border-blue-400/20' : 'bg-brand/5 border border-brand/15'
        }`}>
          <Sparkles className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
          <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-600'}`}>
            <span className="font-semibold text-brand">{t('products.aiTipTitle')}</span>{' '}
            {t('products.aiTipText')}
          </p>
        </div>

        {/* ── Filter-Leiste (Top) ──────────────────────────────────────── */}
        {prefsLoaded && (
          <FilterBar
            availableFilters={availableFilters}
            visibleFilterIds={visibleFilterIds}
            onChangeVisible={handleChangeVisible}
            facets={facets}
            selectedMaterialTypes={selectedMaterialTypes}
            setSelectedMaterialTypes={setSelectedMaterialTypes}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            selectedManufacturers={selectedManufacturers}
            setSelectedManufacturers={setSelectedManufacturers}
            selectedAttrs={selectedAttrs}
            setSelectedAttrs={setSelectedAttrs}
            filteredCount={filteredProducts.length}
            totalCount={(products || []).length}
            hasAnyFilter={hasAnyFilter}
            onResetAll={resetAllFilters}
            isDarkMode={isDarkMode}
            t={t}
            MATERIAL_TYPE_OPTIONS={MATERIAL_TYPE_OPTIONS}
            PRODUCT_STATUS_OPTIONS={PRODUCT_STATUS_OPTIONS}
          />
        )}

        {/* ── Haupt-Layout: Produkt-Grid + SAP-Insights ───────────────── */}
        <div className="grid grid-cols-12 gap-6 items-start">
          {/* Produkt-Grid (volle Breite minus SAP-Insights) */}
          <section className="col-span-12">
            {/* Toolbar: Trefferanzahl + Preis-Spanne + Sortierung (F10) */}
            {/* Sichtbar sobald der Shop Produkte hat — bleibt bei 0 Treffern stehen,
                damit Preis-/Sortier-Kontrollen erreichbar bleiben. */}
            {!loading && (products || []).length > 0 && (
              <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`}>
                  {t('products.showingCount', { shown: displayedProducts.length, total: sortedProducts.length })}
                </p>
                <div className="flex items-center gap-4 flex-wrap">
                  <PriceRangeFilter
                    min={priceMin}
                    max={priceMax}
                    onChange={(lo, hi) => { setPriceMin(lo); setPriceMax(hi); }}
                    isDarkMode={isDarkMode}
                    t={t}
                  />
                  <SortBar
                    value={effectiveSort}
                    onChange={setSortByManual}
                    searchActive={!!searchTerm}
                    isDarkMode={isDarkMode}
                    t={t}
                  />
                </div>
              </div>
            )}
            {searchTerm && (
              <div className={`mb-4 rounded-xl p-3 ${
                isDarkMode ? 'bg-gray-800/30 border border-white/20' : 'bg-white border border-slate-200'
              }`}>
                <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-600'}`}>
                  <span className="font-medium text-brand">{filteredProducts.length}</span>
                  {` ${t('products.filterOf')} `}
                  <span className="font-medium text-brand">{(products || []).length}</span>
                  {` ${t('products.productsWord')}`}
                  {searchTerm && (
                    <span className={`ml-1 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`}>
                      {t('products.filterForTerm')} "{searchTerm}"
                    </span>
                  )}
                </p>
              </div>
            )}

            {loading ? (
              <div className={`${cardCls} p-12`}>
                <div className="flex flex-col items-center justify-center">
                  <div className={`animate-spin rounded-full h-12 w-12 border-[3px] ${
                    isDarkMode ? 'border-blue-400/30 border-t-blue-400' : 'border-slate-200 border-t-brand'
                  } mb-6`}></div>
                  <p className={`${isDarkMode ? 'text-white' : 'text-slate-700'} font-medium`}>
                    {t('products.loading')}
                  </p>
                </div>
              </div>
            ) : filteredProducts.length > 0 ? (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {displayedProducts.map((product, idx) => (
                  <div
                    key={product.id}
                    className={`${cardCls} p-5 h-full flex flex-col group/product transition-all duration-300`}
                  >
                    {/* Bild */}
                    <div
                      onClick={() => onSelectProduct && onSelectProduct(product)}
                      className={`relative h-40 rounded-xl mb-4 flex items-center justify-center overflow-hidden cursor-pointer ${
                        isDarkMode ? 'bg-gray-800/40 border border-white/10' : 'bg-white border border-slate-200'
                      }`}
                    >
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name || t('products.imageAlt')}
                          className="max-w-full max-h-full object-contain group-hover/product:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`${product.image_url ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                        <Package className={`w-12 h-12 ${isDarkMode ? 'text-white/70' : 'text-slate-400'}`} />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 flex flex-col">
                      {product.category && (
                        <div className="mb-1.5">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                            isDarkMode
                              ? 'bg-gray-700/60 text-white/80'
                              : 'bg-brand/5 text-brand/80 border border-brand/15'
                          }`}>
                            <Tag className="w-3 h-3" /> {product.category_label || product.category}
                          </span>
                        </div>
                      )}
                      <h3
                        onClick={() => onSelectProduct && onSelectProduct(product)}
                        className={`font-semibold line-clamp-2 cursor-pointer ${
                          isDarkMode
                            ? 'text-white group-hover/product:text-blue-300'
                            : 'text-slate-900 group-hover/product:text-brand'
                        } transition-colors`}
                      >
                        {product.name || t('products.unnamedProduct')}
                      </h3>
                      <p className={`text-sm line-clamp-2 mt-1 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`}>
                        {product.description || t('products.noDescriptionAvailable')}
                      </p>

                      {/* Preis + Warenkorb */}
                      <div className="mt-auto pt-4 flex items-end justify-between">
                        <div className={`flex items-center text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                          <Euro className="w-4 h-4 mr-1 text-brand" />
                          <span className="text-brand">{parseFloat(product.price || 0).toFixed(2)}</span>
                          <span className={`text-sm ml-1 ${isDarkMode ? 'text-white/70' : 'text-slate-400'}`}>
                            {product.currency || 'EUR'}
                          </span>
                        </div>
                        <LiquidGlassButton
                          onClick={() => onAddToCart(product)}
                          variant="primary"
                          size="small"
                          title={t('products.addToCart')}
                        >
                          <div className="flex items-center">
                            <ShoppingCart className="w-4 h-4 mr-1" />
                            <Plus className="w-3 h-3" />
                          </div>
                        </LiquidGlassButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {canLoadMore && (
                <div className="mt-6 flex justify-center">
                  <LiquidGlassButton
                    onClick={() => setVisibleCount((c) => c + PRODUCTS_PAGE_SIZE)}
                    variant="secondary"
                    size="medium"
                  >
                    {t('products.loadMore')}
                  </LiquidGlassButton>
                </div>
              )}
              </>
            ) : (
              <div className={`${cardCls} p-12`}>
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
                    isDarkMode ? 'bg-gray-700/60 border border-white/15' : 'bg-brand/10'
                  }`}>
                    {searchTerm ? (
                      <Search className={`w-8 h-8 ${isDarkMode ? 'text-white/80' : 'text-brand'}`} />
                    ) : (
                      <Package className={`w-8 h-8 ${isDarkMode ? 'text-white/80' : 'text-brand'}`} />
                    )}
                  </div>
                  <h3 className={`text-xl font-semibold ${headingCls} mb-3`}>{t('products.empty')}</h3>
                  <p className={`${isDarkMode ? 'text-white/70' : 'text-slate-500'} mb-6 max-w-sm mx-auto`}>
                    {t('products.emptyDesc')}
                  </p>
                  {(searchTerm || hasAnyFilter) && (
                    <div className="flex flex-col items-center gap-2">
                      {searchTerm && (
                        <LiquidGlassButton onClick={() => onSearchChange('')} variant="primary" size="medium">
                          {t('products.resetSearch')}
                        </LiquidGlassButton>
                      )}
                      {hasAnyFilter && (
                        <LiquidGlassButton onClick={resetAllFilters} variant="secondary" size="medium">
                          {t('products.filterReset')}
                        </LiquidGlassButton>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
};

export default ProductView;
